import api from './client';

/**
 * Kontakt types
 */
export interface KontaktTag {
  id: number;
  name: string;
  display_name: string;
  description?: string;
}

export interface KontaktAdresse {
  id: number;
  typ: 'privat' | 'geschaeft' | 'rechnung' | 'post';
  typ_display: string;
  strasse: string;
  hausnummer: string;
  zusatz?: string;
  plz: string;
  ort: string;
  land: string;
  is_primary: boolean;
  full_address: string;
  formatted_single_line: string;
}

export interface KontaktTelefon {
  id: number;
  typ: 'mobil' | 'privat' | 'geschaeft' | 'fax';
  typ_display: string;
  nummer: string;
  is_primary: boolean;
}

export interface KontaktEmail {
  id: number;
  typ: 'privat' | 'geschaeft' | 'rechnung';
  typ_display: string;
  email: string;
  is_primary: boolean;
}

export interface Versicherungspolice {
  id: number;
  rsv: number;
  rsv_name: string;
  versicherungsnummer: string;
  deckungsart?: string;
  gueltig_von?: string;
  gueltig_bis?: string;
  is_active: boolean;
  is_valid: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface KontaktListItem {
  id: number;
  uuid: string;
  kontakt_type: 'person' | 'company';
  display_name: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  legal_form?: string;
  iata_code?: string;
  tags: KontaktTag[];
  is_shared: boolean;
  is_active: boolean;
  primary_email?: string;
  primary_phone?: string;
  akten_count: number;
  created_at: string;
  updated_at: string;
}

export interface KontaktDetail extends KontaktListItem {
  anrede?: string;
  date_of_birth?: string;
  registration_number?: string;
  tax_id?: string;
  portal_url?: string;
  formal_name: string;
  addresses: KontaktAdresse[];
  phones: KontaktTelefon[];
  emails: KontaktEmail[];
  versicherungen: Versicherungspolice[];
  user?: number;
  user_id?: number;
  user_email?: string;
  notes?: string;
  organization?: number;
}

export interface KontaktCreateData {
  kontakt_type: 'person' | 'company';
  anrede?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  company_name?: string;
  legal_form?: string;
  registration_number?: string;
  tax_id?: string;
  iata_code?: string;
  portal_url?: string;
  tag_ids?: number[];
  notes?: string;
  is_active?: boolean;
  organization?: number;
  // Inline creation
  address?: Partial<KontaktAdresse>;
  email?: string;
  phone?: string;
}

export interface AkteInfo {
  akte_id: number;
  aktenzeichen: string;
  rolle: string;
  rolle_display: string;
  is_primary: boolean;
  state: string;
  incident_type: string;
  created_at: string;
}

export interface KontaktSearchResult {
  id: number;
  uuid: string;
  kontakt_type: 'person' | 'company';
  display_name: string;
  formal_name: string;
  iata_code?: string;
  primary_email?: string;
  tags: string[];
}

/**
 * Kontakte API calls
 */
export const kontakteAPI = {
  // Tags
  getTags: async (): Promise<KontaktTag[]> => {
    const response = await api.get('/api/tags/');
    // Handle both paginated and non-paginated responses
    return response.data.results || response.data;
  },

  // List kontakte with optional filters
  getKontakte: async (params?: {
    tag?: string;
    type?: 'person' | 'company';
    shared?: boolean;
    active?: boolean;
    search?: string;
  }): Promise<KontaktListItem[]> => {
    const response = await api.get('/api/kontakte/', { params });
    // Handle paginated response - extract results array
    return response.data.results || response.data;
  },

  // Get single kontakt
  getKontaktById: async (id: number): Promise<KontaktDetail> => {
    const response = await api.get(`/api/kontakte/${id}/`);
    return response.data;
  },

  // Create kontakt
  createKontakt: async (data: KontaktCreateData): Promise<KontaktDetail> => {
    const response = await api.post('/api/kontakte/', data);
    return response.data;
  },

  // Update kontakt
  updateKontakt: async (id: number, data: Partial<KontaktDetail>): Promise<KontaktDetail> => {
    const response = await api.patch(`/api/kontakte/${id}/`, data);
    return response.data;
  },

  // Delete kontakt
  deleteKontakt: async (id: number): Promise<void> => {
    await api.delete(`/api/kontakte/${id}/`);
  },

  // Search kontakte (for autocomplete)
  searchKontakte: async (query: string, options?: {
    tag?: string;
    limit?: number;
  }): Promise<KontaktSearchResult[]> => {
    const params = { q: query, ...options };
    const response = await api.get('/api/kontakte/search/', { params });
    return response.data;
  },

  // Get akten for kontakt
  getKontaktAkten: async (kontaktId: number): Promise<AkteInfo[]> => {
    const response = await api.get(`/api/kontakte/${kontaktId}/akten/`);
    return response.data;
  },

  // Address management
  getAddresses: async (kontaktId: number): Promise<KontaktAdresse[]> => {
    const response = await api.get(`/api/kontakte/${kontaktId}/adressen/`);
    return response.data;
  },

  createAddress: async (kontaktId: number, data: Partial<KontaktAdresse>): Promise<KontaktAdresse> => {
    const response = await api.post(`/api/kontakte/${kontaktId}/adressen/`, data);
    return response.data;
  },

  updateAddress: async (kontaktId: number, addressId: number, data: Partial<KontaktAdresse>): Promise<KontaktAdresse> => {
    const response = await api.patch(`/api/kontakte/${kontaktId}/adressen/${addressId}/`, data);
    return response.data;
  },

  deleteAddress: async (kontaktId: number, addressId: number): Promise<void> => {
    await api.delete(`/api/kontakte/${kontaktId}/adressen/${addressId}/`);
  },

  // Phone management
  getPhones: async (kontaktId: number): Promise<KontaktTelefon[]> => {
    const response = await api.get(`/api/kontakte/${kontaktId}/telefone/`);
    return response.data;
  },

  createPhone: async (kontaktId: number, data: Partial<KontaktTelefon>): Promise<KontaktTelefon> => {
    const response = await api.post(`/api/kontakte/${kontaktId}/telefone/`, data);
    return response.data;
  },

  updatePhone: async (kontaktId: number, phoneId: number, data: Partial<KontaktTelefon>): Promise<KontaktTelefon> => {
    const response = await api.patch(`/api/kontakte/${kontaktId}/telefone/${phoneId}/`, data);
    return response.data;
  },

  deletePhone: async (kontaktId: number, phoneId: number): Promise<void> => {
    await api.delete(`/api/kontakte/${kontaktId}/telefone/${phoneId}/`);
  },

  // Email management
  getEmails: async (kontaktId: number): Promise<KontaktEmail[]> => {
    const response = await api.get(`/api/kontakte/${kontaktId}/emails/`);
    return response.data;
  },

  createEmail: async (kontaktId: number, data: Partial<KontaktEmail>): Promise<KontaktEmail> => {
    const response = await api.post(`/api/kontakte/${kontaktId}/emails/`, data);
    return response.data;
  },

  updateEmail: async (kontaktId: number, emailId: number, data: Partial<KontaktEmail>): Promise<KontaktEmail> => {
    const response = await api.patch(`/api/kontakte/${kontaktId}/emails/${emailId}/`, data);
    return response.data;
  },

  deleteEmail: async (kontaktId: number, emailId: number): Promise<void> => {
    await api.delete(`/api/kontakte/${kontaktId}/emails/${emailId}/`);
  },

  // Versicherungspolice management
  getVersicherungen: async (kontaktId: number): Promise<Versicherungspolice[]> => {
    const response = await api.get(`/api/kontakte/${kontaktId}/versicherungen/`);
    return response.data;
  },

  createVersicherung: async (kontaktId: number, data: Partial<Versicherungspolice>): Promise<Versicherungspolice> => {
    const response = await api.post(`/api/kontakte/${kontaktId}/versicherungen/`, data);
    return response.data;
  },

  updateVersicherung: async (kontaktId: number, versicherungId: number, data: Partial<Versicherungspolice>): Promise<Versicherungspolice> => {
    const response = await api.patch(`/api/kontakte/${kontaktId}/versicherungen/${versicherungId}/`, data);
    return response.data;
  },

  deleteVersicherung: async (kontaktId: number, versicherungId: number): Promise<void> => {
    await api.delete(`/api/kontakte/${kontaktId}/versicherungen/${versicherungId}/`);
  },
};
